import { UTMParams, TrackingParams, StoredTrackingParams } from './tracking.types';

export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
export const TRACKING_KEYS = ['gclid', 'fbclid', 'msclkid', 'ttclid', 'liFatId', 'twclid', 'gbraid', 'wbraid'] as const;
export const REFERRER_KEYS = ['utm_id', 'referrer_url', 'referrer_domain'] as const;

export const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  // Regex: match cookie by name — `(^|;)` handles start or separator, `\\s*=\\s*` allows spaces around `=`, `([^;]+)` captures the value up to the next `;`
  const match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return match ? decodeURIComponent(match.pop()!) : null;
};

export const setCookie = (name: string, value: string, days = 120): void => {
  if (typeof document === 'undefined') return;
  const maxAge = 60 * 60 * 24 * days; // 120 days
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}`;
};

export const captureTrackingParams = (): void => {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const allTrackingKeys = [...UTM_KEYS, ...TRACKING_KEYS, ...REFERRER_KEYS];

  allTrackingKeys.forEach((key) => {
    const fromUrl = params.get(key);
    const fromLS = localStorage.getItem(key);
    const fromCookie = getCookie(key);

    // Only set if not already stored
    if (fromUrl && !fromLS && !fromCookie) {
      localStorage.setItem(key, fromUrl);
      setCookie(key, fromUrl);
    }
  });

  // LinkedIn uses li_fat_id in URLs/cookies; store under liFatId for API mapping
  const liFatIdFromUrl = params.get('li_fat_id');
  if (liFatIdFromUrl && !localStorage.getItem('liFatId') && !getCookie('liFatId')) {
    localStorage.setItem('liFatId', liFatIdFromUrl);
    setCookie('liFatId', liFatIdFromUrl);
  }

  // Store referrer information automatically
  if (!localStorage.getItem('referrer_url') && !getCookie('referrer_url')) {
    const referrerUrl = document.referrer;
    if (referrerUrl) {
      localStorage.setItem('referrer_url', referrerUrl);
      setCookie('referrer_url', referrerUrl);

      // Extract and store referrer domain
      try {
        const referrerDomain = new URL(referrerUrl).hostname;
        localStorage.setItem('referrer_domain', referrerDomain);
        setCookie('referrer_domain', referrerDomain);
      } catch (e) {
        // Invalid referrer URL
      }
    }
  }
};

export const getStoredUTMParams = (): StoredTrackingParams => {
  const utm: UTMParams = {};

  const utmMappings: Record<string, keyof UTMParams> = {
    utm_source: 'source',
    utm_medium: 'medium',
    utm_campaign: 'campaign',
    utm_term: 'term',
    utm_content: 'content',
  };

  Object.entries(utmMappings).forEach(([storageKey, utmKey]) => {
    const fromLS = localStorage.getItem(storageKey);
    const fromCookie = getCookie(storageKey);

    const value = fromLS || fromCookie;
    if (value) utm[utmKey] = value;
  });

  const tracking: TrackingParams = {};

  const trackingMappings: Record<string, keyof TrackingParams> = {
    gclid: 'gclid',
    fbclid: 'fbclid',
    msclkid: 'msclkid',
    ttclid: 'ttclid',
    liFatId: 'liFatId',
    twclid: 'twclid',
    gbraid: 'gbraid',
    wbraid: 'wbraid',
    utm_id: 'utmId',
    referrer_url: 'referrerUrl',
    referrer_domain: 'referrerDomain',
  };

  Object.entries(trackingMappings).forEach(([storageKey, trackingKey]) => {
    const fromLS = localStorage.getItem(storageKey);
    const fromCookie = getCookie(storageKey);

    const value = fromLS || fromCookie;
    if (value) tracking[trackingKey] = value;
  });

  const result: StoredTrackingParams = { utm };

  if (Object.keys(tracking).length > 0) {
    result.tracking = tracking;
  }

  return result;
};
